import { useState } from "react";
import { useSession } from "@/context/SessionContext";
import { post } from "@/lib/api";
import { DemoCodeNote } from "@/pages/Doorway";
import { IllustrativeBadge } from "@/components/IllustrativeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, RotateCcw } from "lucide-react";
import { NeedsAttention } from "@/components/desk/NeedsAttention";
import { TodayPanel } from "@/components/desk/TodayPanel";
import { PeopleOnboarding } from "@/components/desk/PeopleOnboarding";
import { GovernedRegister } from "@/components/desk/GovernedRegister";
import { RoomsActivities } from "@/components/desk/RoomsActivities";
import { EvidenceReports } from "@/components/desk/EvidenceReports";

const TABS = [
  { id: "today", label: "Today" },
  { id: "people", label: "People & onboarding" },
  { id: "policies", label: "Policies & external duties" },
  { id: "rooms", label: "Rooms & activities" },
  { id: "evidence", label: "Evidence & reports" },
];

export default function StaffWorkspace() {
  const { session, signIn } = useSession();
  const authed = session?.role === "centre_admin" || session?.role === "booking";
  const [code, setCode] = useState("");

  if (!authed) {
    const enter = () => {
      const role = signIn(code);
      if (role === "centre_admin" || role === "booking") toast.success("Demo session started (synthetic).");
      else toast.error("Use a staff demo code: admin or booking.");
    };
    return (
      <div className="mx-auto max-w-lg px-5 py-16 sm:px-8" data-testid="staff-gate">
        <IllustrativeBadge className="mb-4" />
        <h1 className="font-display text-2xl font-bold text-mill-ink">Mill Control Desk</h1>
        <p className="mt-1 text-sm text-mill-muted">Enter a demo access code to continue.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-mill-muted"><KeyRound className="mr-1 inline h-3.5 w-3.5" />Demo access code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && enter()} placeholder="admin or booking" className="w-56" data-testid="staff-gate-input" />
          </div>
          <Button className="h-10 bg-mill-moss hover:bg-mill-moss-dark" onClick={enter} data-testid="staff-gate-submit">Enter</Button>
        </div>
        <DemoCodeNote />
      </div>
    );
  }
  return <ControlDesk isAdmin={session.role === "centre_admin"} />;
}

function ControlDesk({ isAdmin }) {
  const [tab, setTab] = useState("today");
  const [refreshKey, setRefreshKey] = useState(0);
  const [resetting, setResetting] = useState(false);
  const bump = () => setRefreshKey((k) => k + 1);
  const resetDemo = async () => {
    setResetting(true);
    try { const r = await post("/desk/reseed", {}); toast.success(r.message || "Demo data reset."); bump(); }
    catch { toast.error("Could not reset demo data."); }
    setResetting(false);
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8" data-testid="control-desk">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3"><IllustrativeBadge /><span className="text-sm font-semibold text-mill-moss">{isAdmin ? "Centre Administrator" : "Booking Administrator"}</span></div>
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={resetDemo} disabled={resetting} data-testid="reset-demo-data">
            <RotateCcw className={`mr-1.5 h-3.5 w-3.5 ${resetting ? "animate-spin" : ""}`} /> Reset demo data
          </Button>
        )}
      </div>
      <h1 className="font-display text-3xl font-bold text-mill-ink sm:text-4xl">Mill Control Desk</h1>
      <p className="mt-2 text-mill-muted">Today's operational picture: people, practice, places and evidence.</p>

      <div className="mt-8"><NeedsAttention onNavigate={setTab} refreshKey={refreshKey} /></div>

      <div className="mb-6 flex flex-wrap gap-1.5 border-b border-stone-200" data-testid="desk-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`-mb-px whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors ${tab === t.id ? "border-mill-moss text-mill-ink" : "border-transparent text-mill-muted hover:text-mill-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div data-testid={`desk-panel-${tab}`} key={`${tab}-${refreshKey}`}>
        {tab === "today" && <TodayPanel onNavigate={setTab} />}
        {tab === "people" && <PeopleOnboarding isAdmin={isAdmin} onChanged={bump} />}
        {tab === "policies" && <GovernedRegister isAdmin={isAdmin} onChanged={bump} />}
        {tab === "rooms" && <RoomsActivities onChanged={bump} />}
        {tab === "evidence" && <EvidenceReports />}
      </div>
    </div>
  );
}
